import type DemandOption from "@/domains/DemandsV2/DemandOption";
import type DemandRequest from "@/domains/DemandsV2/DemandRequest";
import type DemandV2 from "@/domains/DemandsV2/DemandV2";
import type Offer from "@/domains/Offers/Offer";
import type Demand from "@/domains/Demand/Demand";
import SerializeSimplified from "@/core/misc/decorators/SerializeSimplified";
import NoSerialize from "@/core/misc/decorators/NoSerialize";
import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";
import Title_ru_RU from "@/core/misc/decorators/Title_ru_RU";
import TYPES from "@/core/services/Changelogs/types";
import Core from "@/core/Core";
import DateSimple from '@/core/types/DateSimple';
import DateRange from '@/core/types/DateRange';
import TimeStamp from "@/core/types/TimeStamp";
import Sum from '@/core/types/Sum';
import Qty from '@/core/types/Qty';
import AbstractOrderPosition from '@/domains/Abstract/AbstractOrderPosition';
import OrderPositionShipmentStatus from '@/domains/Orders/OrderPositionShipmentStatus';
import OrderPositionPaymentStatus from '@/domains/Orders/OrderPositionPaymentStatus';
import PreorderPosition from "@/domains/Preorders/PreorderPosition";
import TenderBid from "@/domains/TenderBids/TenderBid";
import Tender from "@/domains/Tenders/Tender";
import OrderRequisites from "@/domains/Orders/OrderRequisites";


@Title_ru_RU_Class('Позиция заказа')
class OrderPosition extends AbstractOrderPosition {
  static Events = {
    Created: 'orderPosition:created',
    Updated: 'orderPosition:updated',
    Deleted: 'orderPosition:deleted',
  } as const;

  static ReasonUnavailable = {
    // Расширяем причины родителя своими — спред сохраняет ключи
    // AbstractOrderEntity (наследуются через AbstractOrderPosition),
    // поэтому override статика совместим по типу.
    ...AbstractOrderPosition.ReasonUnavailable,
    NOT_IN_PROCESS: 'Позиция еще не оплачена или не отгружена',
    ALREADY_IN_PROCESS: 'Позиция уже оплачена или отгружена',
    ALREADY_ASSEMBLED: 'По позиции заказа есть отгрузка/сборка',
    ALREADY_PAYED: 'По позиции заказа есть оплата, или она стоит в очереди на оплату',
    NONE_AVAILABLE: 'Всё количество уже собрано или отгружено',
    IS_DONE: 'Количество в работе по позиции равно нулю',
    IS_PREORDER: 'Нельзя вернуть позицию предзаказа',
    IS_DRAFT: 'Позиция ещё черновик',
    IS_SHIPPED: 'По позиции есть отгрузка',
    IS_PAYED: 'По позиции есть оплата',
    EXISTS_ON_BACKEND: 'Позиция уже сохранена',
  } as const;

  static StatusNames = {
    'in-work': 'В работе',
    'finished': 'Завершён',
    'received': 'Получен',
    'deleted': 'Удалён',
    'draft': 'Черновик',
  } as const;

  // ---- Ссылки ----
  @SerializeSimplified
  public demandV2: DemandV2 | { id: number } | null;

  @SerializeSimplified
  public demandRequest: DemandRequest | { id: number } | null;

  @SerializeSimplified
  public demandOption: DemandOption | { id: number } | null;

  @SerializeSimplified
  public preorderPosition: PreorderPosition | { id: number } | null;

  // ---- Даты ----
  @Title_ru_RU('желаемая дата получения', TYPES.DATE)
  private _desiredReceiptDate: DateSimple;
  @Title_ru_RU('плановая дата отгрузки', TYPES.DATE)
  private _plannedShipmentDate: DateSimple;
  @Title_ru_RU('плановая дата платежа', TYPES.DATE)
  public plannedPaymentDate: DateSimple;

  // ---- Статусы ----
  public shipmentStatus: OrderPositionShipmentStatus;
  @NoSerialize
  public paymentStatus: OrderPositionPaymentStatus;

  /** Комментарии к позиции*/
  public commentaries: any[] = [];

  static fromServer(data: any, result = new OrderPosition()): OrderPosition {
    super.fromServer(data, result);

    //
    if (result.order) {
      result.order.createdAt = Core.utils.hydrate(data.order.createdAt, TimeStamp.fromServer);
    }

    // ---- Даты ----
    /** Пишем в поля, а не через сеттеры: обе даты пришли с бэка, и пересчёт
     *  одной из другой затёр бы то, что прислали. */
    result._desiredReceiptDate = Core.utils.hydrate(data.desiredReceiptDate, DateSimple.fromServer)!;
    result._plannedShipmentDate = Core.utils.hydrate(data.plannedShipmentDate, DateSimple.fromServer)!;
    result.plannedPaymentDate = Core.utils.hydrate(data.plannedPaymentDate, DateSimple.fromServer)!;

    // ---- Статусы ----
    result.shipmentStatus = Core.utils.hydrate(data.shipmentStatus, OrderPositionShipmentStatus.fromServer)!;
    result.paymentStatus = Core.utils.hydrate(data.paymentStatus, OrderPositionPaymentStatus.fromServer)!;

    return result;
  }

  /**
   * Позиция заказа из опции потребности V2.
   * Ссылки на источник и даты — здесь, товар и цены — в предке.
   */
  static fromDemandV2Option(
    request: DemandRequest,
    usedOption: { option: DemandOption, usedQty: Qty },
    result = new OrderPosition(),
  ): OrderPosition {
    super.fromDemandV2Option(usedOption, result);

    // ---- Ссылки ----
    result.demandV2 = usedOption.option.demand;
    result.demandRequest = request;
    result.demandOption = usedOption.option;

    // ---- Даты ----
    /** Плановую отгрузку проставит сеттер — отнимет срок доставки. */
    result.desiredReceiptDate = request.desiredReceiptDate!;

    return result;
  }

  /**
   * Позиция заказа из предложения.
   * Товар и цены — в предке, здесь только ссылки на источник.
   */
  static fromOffer(offer: Offer, demand: Demand | null = null, result = new OrderPosition()): OrderPosition {
    super.fromOffer(offer, demand, result);

    return result;
  }

  /**
   * Позиция заказа из позиции предзаказа.
   * Товар и цены — в предке, здесь ссылка на источник.
   */
  static fromPreorderPosition(preorderPosition: PreorderPosition, result = new OrderPosition()): OrderPosition {
    super.fromPreorderPosition(preorderPosition, result);

    result.preorderPosition = preorderPosition;

    return result;
  }

  /**
   * Клон позиции из истории закупок для добавления в заказ.
   * Берём почти всё (товар, коды, количество, цены, НДС, температуру),
   * но сбрасываем идентичность и всё, что не должно переезжать в новую позицию:
   * статусы, расчётные accounting-поля, ссылки на источник.
   * Реквизиты НЕ клонируем — их выдаст заказ в addPosition (позиция «под заказ»).
   */
  static fromOrderPosition(source: OrderPosition): OrderPosition {
    const result = Core.utils.cloneDeep(source);

    // новая сущность для бэка — сбрасываем id/version, иначе копия уйдёт на
    // сохранение под чужим id и перезапишет исходную позицию
    result.resetIdentity();
    // связь со строкой реестра-источника держит отдельное поле, а не id:
    // по нему реестр показывает добавленные позиции отмеченными
    result.sourcePositionId = source.id;
    result.isDraft = true;

    // Сброс полей, которые не должны переезжать в новую позицию.
    // Object.assign не проверяет nullability отдельных полей, а satisfies ловит опечатки в именах.
    Object.assign(result, {
      // ---- Ссылки на источник не переносим ----
      order: null,
      offer: null,
      demand: null,
      demandV2: null,
      demandRequest: null,
      demandOption: null,
      preorderPosition: null,

      // ---- Статусы прошлой позиции не переносим ----
      shipmentStatus: undefined,
      paymentStatus: undefined,

      // ---- Даты прошлой позиции не переносим ----
      desiredReceiptDate: undefined,
      plannedShipmentDate: undefined,
      plannedPaymentDate: undefined,
    } satisfies Partial<Record<keyof OrderPosition, unknown>>);

    return result;
  }

  /* в тендере приходят ужатые orderPositions */
  static fromTender(source: any): OrderPosition {
    const result = new OrderPosition();
    result.requisites = new OrderRequisites();

    result.id = source.id;
    result.isPreorder = source.isPreorder;
    result.qty = Qty.fromServer(source.qty);

    return result;
  }

  static fromTenderBid(tenderBid: TenderBid, tender: Tender, result = new OrderPosition()) {
    super.fromTenderBid(tenderBid, tender, result);

    // у тендера может не быть ждд
    if (tender.desiredReceiptDate) {
      /** Плановую отгрузку проставит сеттер — отнимет срок доставки. */
      result.desiredReceiptDate = tender.desiredReceiptDate;
    }

    return result;
  }

  get hasVatRateSellerSource(): boolean {
    return super.hasVatRateSellerSource
      || Core.utils.isNullOrUndefined(this.demandV2) === false
      || Core.utils.isNullOrUndefined(this.preorderPosition) === false;
  }

  get desiredReceiptDate(): DateSimple { return this._desiredReceiptDate }
  set desiredReceiptDate(value: DateSimple) {
    this._desiredReceiptDate = value;

    if (
      Core.utils.isNullOrUndefined(value)
      || Core.utils.isNullOrUndefined(this.requisites?.deliveryDays)
    ) {
      // без даты или без срока доставки плановую отгрузку не вычислить
    }
    else {
      this._plannedShipmentDate = value.subtract({ days: this.requisites.deliveryDays });
    }
  }

  get plannedShipmentDate(): DateSimple { return this._plannedShipmentDate }
  set plannedShipmentDate(value: DateSimple) {
    this._plannedShipmentDate = value;

    if (
      Core.utils.isNullOrUndefined(value)
      || Core.utils.isNullOrUndefined(this.requisites?.deliveryDays)
    ) {
      // без даты или без срока доставки желаемое получение не вычислить
    }
    else {
      this._desiredReceiptDate = value.addDays(this.requisites.deliveryDays);
    }
  }

  /**
   * Период получения: плановая отгрузка плюс срок доставки. У позиции заказа
   * это одна дата, а не окно, поэтому границы совпадают.
   */
  get receiptPeriod(): DateRange | null {
    if (
      Core.utils.isNullOrUndefined(this._plannedShipmentDate) === true
      || Core.utils.isNullOrUndefined(this.requisites?.deliveryDays) === true
    ) {
      return null;
    }
    else {
      const receiptDate = this._plannedShipmentDate.addDays(this.requisites.deliveryDays);

      return new DateRange(receiptDate, receiptDate);
    }
  }

  get unpaidSum(): Sum | undefined {
    if (this.paymentStatus) {
      return this.paymentStatus.total.minus(this.paymentStatus.inQueue).minus(this.paymentStatus.payed);
    } else {
      return undefined;
    }
  }

  get status(): OrderPositionStatus {
    if (this.isDeleted) {
      return 'deleted';
    }
    if (this.isDraft) {
      return 'draft';
    }
    if (this.requisites.isFinished) {
      return 'finished';
    }
    if (this.shipmentStatus?.receivedByBuyer && this.shipmentStatus.receivedByBuyer.compare(this.qty) !== -1) {
      return 'received';
    }
    return 'in-work';
  }

  get statusName(): string {
    return OrderPosition.StatusNames[this.status];
  }

  // get accountingCurrencyRate() {
  //   return
  // }

  /**
   * Причины, по которым нельзя менять собственные поля позиции — цену,
   * валюту, ставку НДС, наименование, условия перевозки.
   * Пустой массив — менять можно.
   *
   * Происхождение позиции (isOfferBased, demandV2) здесь не участвует:
   * к консистентности заказа и его позиций оно отношения не имеет.
   *
   * shipped/payed — Numeric (Qty/Sum), сравнение через isPositive():
   * `> 0` спровоцировало бы valueOf() и исключение неявного приведения.
   */
  getReasonsCannotChangePosition(): string[] {
    const reasons: string[] = [];

    if (this.shipmentStatus?.shipped.isPositive() === true) {
      reasons.push(OrderPosition.ReasonUnavailable.IS_SHIPPED);
    }

    if (this.paymentStatus?.payed.isPositive() === true) {
      reasons.push(OrderPosition.ReasonUnavailable.IS_PAYED);
    }

    return reasons;
  }

  /**
   * Причины, по которым нельзя сменить товар позиции.
   * Товар задаётся при создании: смена товара у сохранённой позиции — это
   * другая позиция, а не правка этой.
   */
  getReasonsCannotChangeProduct(): string[] {
    const reasons: string[] = [];

    if (this.isExisting === true) {
      reasons.push(OrderPosition.ReasonUnavailable.EXISTS_ON_BACKEND);
    }

    return reasons;
  }

  validateDesiredReceiptDate(value: DateSimple): null | string {
    if (this.isExisting) return null;
    if (Core.utils.isNullOrUndefined(this.requisites.deliveryDays)) return null; // если дни доставки не указаны

    const expectedDate = new DateSimple().addDays(this.requisites.deliveryDays);

    if (value >= expectedDate) return null;

    return `Нельзя указать раньше, чем смогут доставить. Минимальная возможная дата ${Core.utils.date.format(expectedDate)}`;
  }

  getReasonCannotNotify(): string | null {
    if (this.isDraft) {
      return OrderPosition.ReasonUnavailable.IS_DRAFT;
    }

    const reason = super.getReasonCannotNotify();
    if (reason) return reason;

    if (this.shipmentStatus.shipped.isPositive()
      || this.shipmentStatus.receivedByBuyer.isPositive()
      || this.paymentStatus.inQueue.isPositive()
      || this.paymentStatus.payed.isPositive()
    ) {
      return OrderPosition.ReasonUnavailable.ALREADY_IN_PROCESS;
    }

    return null;
  }

  getReasonCannotDelete(): string | null {
    if (Core.currentPartner.isSeller === true) {
      return OrderPosition.ReasonUnavailable.NO_ACCESS;
    }

    if (this.isDraft) {
      return OrderPosition.ReasonUnavailable.IS_DRAFT;
    }

    const reason = super.getReasonCannotDelete();
    if (reason) return reason;

    if (this.shipmentStatus.shipped.isPositive()
      || this.shipmentStatus.receivedByBuyer.isPositive()
      || this.paymentStatus.inQueue.isPositive()
      || this.paymentStatus.payed.isPositive()
    ) {
      return OrderPosition.ReasonUnavailable.ALREADY_IN_PROCESS;
    }

    return null;
  }

  getReasonCannotClose(): string | null {
    if (this.isDraft) {
      return OrderPosition.ReasonUnavailable.IS_DRAFT;
    }

    const reason = super.getReasonCannotClose();
    if (reason) return reason;

    if (
      this.shipmentStatus.shipped.isZero()
      && this.shipmentStatus.receivedByBuyer.isZero()
      && this.paymentStatus.inQueue.isZero()
      && this.paymentStatus.payed.isZero()
    ) {
      return OrderPosition.ReasonUnavailable.NOT_IN_PROCESS;
    }

    if (this.shipmentStatus.inWork.isZero()) {
      return OrderPosition.ReasonUnavailable.IS_DONE;
    }

    return null;
  }

  getReasonCannotUnconfirm(): string | null {
    if (this.isDraft) {
      return OrderPosition.ReasonUnavailable.IS_DRAFT;
    }

    const reason = super.getReasonCannotUnconfirm();
    if (reason) return reason;

    if (
      this.shipmentStatus.shipped.isPositive()
      || this.shipmentStatus.assembled.isPositive()
    ) {
      return OrderPosition.ReasonUnavailable.ALREADY_ASSEMBLED;
    }

    if (this.paymentStatus.payed.isPositive()
      || this.paymentStatus.inQueue.isPositive()
    ) {
      return OrderPosition.ReasonUnavailable.ALREADY_PAYED;
    }

    if (this.preorderPosition && this.preorderPosition?.id) {
      return OrderPosition.ReasonUnavailable.IS_PREORDER;
    }

    return null;
  }

  getReasonCannotCreatePayment(): string | null {
    if (this.isDraft) {
      return OrderPosition.ReasonUnavailable.IS_DRAFT;
    }

    const unpaidSum = this.unpaidSum;
    if (unpaidSum && !unpaidSum.isPositive()) {
      return OrderPosition.ReasonUnavailable.ALREADY_PAYED;
    }

    return null;
  }

  /**
   * Причины, по которым нельзя менять реквизиты сделки на позиции.
   * Позиция заказа на основе предзаказа (заполнен preorderPosition) - редактируем,
   * обычная позиция заказа - нет, идем в карточку заказа, чтобы это поменять в согласии с другими позициями в заказе
   */
  getReasonsCannotChangeDealTerms(): string[] {
    if (Core.utils.isNullOrUndefined(this.preorderPosition?.id) === false) {
      return [];
    }
    else {
      return super.getReasonsCannotChangeDealTerms();
    }
  }
}

export type OrderPositionStatus = keyof typeof OrderPosition.StatusNames;

export default OrderPosition;
